/**
 * Deleo delivery tracking status codes
 * These are the status codes returned from the Deleo API
 */
export enum DeliveryTrackingStatus {
  Delivered = 'D06',
  CustomsCleared = 'I04',
  InCustoms = 'I03',
  ArrivedDestination = 'I02',
  Departed = 'I01',
  InvoiceIssued = 'I00',
}

export const DELIVERED_STATUS = DeliveryTrackingStatus.Delivered;

/**
 * PARXL delivery tracking status codes
 * These are the status strings returned from the PARXL API
 */
export enum ParxlTrackingStatus {
  // Booking statuses
  Booked = 'Booked',
  Cancelled = 'Cancelled',
  Expired = 'Expired',
  BookingReceived = 'Booking received',
  BookingRejected = 'Booking rejected',

  // Pickup & Processing
  PickedUp = 'Picked up',
  ReceivedAtExportHub = 'Received at parxl export hub',
  ProcessingAtExportHub = 'Processing at parxl export hub',

  // Transit
  ReadyToTransfer = 'Ready to transfer to airline',
  ReceivedAtAirlineTerminal = 'Received at airline terminal',
  DepartedFromOrigin = 'Departed from origin airport',
  InTransit = 'In transit',

  // Arrival
  ArrivedAtDestinationAirport = 'Arrived at destination airport',
  ArrivedAtCustomsFacility = 'Arrived at destination customs facility',
  EnrouteToSortingHub = 'Enroute to destination sorting hub',
  ArrivedAtSortingHub = 'Arrived and processed at sorting hub',
  ArrivedAndProcessingAtSortingHub = 'Arrived and processing at sorting hub',

  // Customs
  HandedOverForCustoms = 'Handed over for customs clearance',
  CustomCleared = 'Custom cleared at destination',

  // Delivery
  OutForDelivery = 'Out for delivery',
  Delivered = 'Delivered',
  AwaitingCollection = 'Awaiting collection',

  // Delivery Issues
  UnsuccessfulDelivery = 'Unsuccessful delivery',
  FinalUnsuccessfulDelivery = 'Final unsuccessful delivery',
  PossibleDelay = 'Possible delay from last mile',

  // Exceptions
  Disposed = 'Disposed',
  DamagedAtDestination = 'Damaged at destination',
  Damaged = 'Damaged',
  Lost = 'Lost',

  // Modifications
  Relabelled = 'Relabelled',
  Repackaged = 'Repackaged',
  Translated = 'Translated',
  AddressChange = 'Address change',
}

export const PARXL_DELIVERED_STATUS = ParxlTrackingStatus.Delivered;

/**
 * YunExpress delivery tracking node codes (track_node_code)
 * Official Trajectory Node Table from YunExpress Track API
 * @see https://developer.yunexpress.com
 */
export const YunExpressTrackingStatus = {
  // Delivered
  Delivered: 'DELIVERED',

  // Return
  Returned: 'RETURNED',
  ReturnedBack: 'RETURNED_BACK',
  ReturnedToSender: 'RETURNED_TO_SENDER',

  // In transit (normal)
  OrderCreation: 'ORDER_CREATION',
  PickedUp: 'PICKED_UP',
  PreInfo: 'PRE_INFO',
  PreAdvicing: 'PRE_ADVICING',
  FirstMileArrive: 'FIRST_MILE_ARRIVE',
  FirstMileDepart: 'FIRST_MILE_DEPART',
  DepartConfirmOC: 'DEPART_CONFIRM_OC',
  ArriveConfirmOC: 'ARRIVE_CONFIRM_OC',
  MainLineDepart: 'MAIN_LINE_DEPART',
  MainLineArrive: 'MAIN_LINE_ARRIVE',
  InTransit: 'IN_TRANSIT',
  TransitIn: 'TRANSIT_IN',
  TransitOut: 'TRANSIT_OUT',
  TransithubArrive: 'TRANSITHUB_ARRIVE',
  ChangeWaybillInfo: 'CHANGE_WAYBILL_INFO',
  Edd: 'EDD',
  PickupCargoTerminal: 'PICKUP_CARGO_TERMINAL',

  // Customs
  ArriveOrinCustoms: 'ARRIVE_ORIN_CUSTOMS',
  CustomsProcessing: 'CUSTOMS_PROCESSING',
  CustomsComplete: 'CUSTOMS_COMPLETE',
  CustomsRelease: 'CUSTOMS_RELEASE',
  ExportCustomsComplete: 'EXPORT_CUSTOMS_COMPLETE',
  AirportRelease: 'AIRPORT_RELEASE',

  // In transportation (last mile)
  CarrierPickup: 'CARRIER_PICKUP',
  InTransitCarrier: 'IN_TRANSIT_CARRIER',
  DeliveryAttempt: 'DELIVERY_ATTEMPT',

  // Abnormal / Exception
  AirportHold: 'AIRPORT_HOLD',
  AirportInspection: 'AIRPORT_INSPECTION',
  CustomsDelay: 'CUSTOMS_DELAY',
  CustomsHold: 'CUSTOMS_HOLD',
  CustomsInspection: 'CUSTOMS_INSPCTION',
  DeliveryFailure: 'DELIVERY_FAILURE',
  PackageException: 'PACKAGE_EXCEPTION',
  PackageLost: 'PACKAGE_LOST',
  Other: 'OTHER',
} as const;

export type YunExpressTrackingStatus = typeof YunExpressTrackingStatus[keyof typeof YunExpressTrackingStatus];

export const YUN_EXPRESS_DELIVERED_STATUS = YunExpressTrackingStatus.Delivered;
